import React, { useState } from 'react';
import styled from 'styled-components';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button, Grid, Card, CardContent, Typography } from '@material-ui/core';
import BallotOutlinedIcon from '@material-ui/icons/BallotOutlined';
import ZoomOutMapOutlinedIcon from '@material-ui/icons/ZoomOutMapOutlined';
import AppsOutlinedIcon from '@material-ui/icons/AppsOutlined';
import { useDispatch } from 'react-redux';

import { SheetKinds, generateSheetId, addSheet, setDefaultSheet } from '../redux/sheetDbSlice';
import { addSheetInformation } from '../redux/sheetInformationDbSlice';
import { addSheetTree, updateSheetTree } from '../redux/sheetTreeDbSlice';
import useCurrentDb from '../hooks/useCurrentDb';
import useSheetTreeDbsInBook from '../hooks/useSheetTreeDbsInBook';
import { WorkAreaStateType } from './BookEditor';

export type AddSheetRelation = 'brother' | 'child' | 'noParent'

const SheetTypeCard = styled(Card)`
  text-align: center;
  transition: background-color 300ms;
  &:hover {
    background-color: #f5f5f5;
    cursor: pointer;
  }
`
const StoryBallotOutlinedIcon = styled(BallotOutlinedIcon)`
  color: grey;
  font-size: 200px;
`
const MapZoomOutMapOutlinedIcon = styled(ZoomOutMapOutlinedIcon)`
  color: grey;
  font-size: 200px;
`
const GridAppsOutlinedIcon = styled(AppsOutlinedIcon)`
  color: grey;
  font-size: 200px;
`
const BlueTypography = styled(Typography)`
  color: #1e88e5;
`

type AddSheetDialogProps = {
  open: boolean;
  onClose: () => void;
  workAreaState: WorkAreaStateType;
  nodeClicked: (workArea: WorkAreaStateType, id?: string) => void;
  relation?: AddSheetRelation;
}
const AddSheetDialog: React.FC<AddSheetDialogProps> = ({ open, onClose, workAreaState, nodeClicked, relation }) => {
  const dispatch = useDispatch();
  const [sheetId, bookId] = useCurrentDb();
  const [sheetTreeIds, sheetTrees] = useSheetTreeDbsInBook(bookId);
  const [unclickable, setUnclickable] = useState(false);
  const getSheetLength = (): number => {
    if (workAreaState === 'bookCover' || relation === 'noParent') {
      const brotherIds = sheetTrees.filter(sheetTree => !sheetTree.parentSheetId);
      return brotherIds.length;
    } else if (relation === 'brother') {
      const sheetTree = sheetTrees.find(sheetTree => sheetTree.sheetId === sheetId);
      if (sheetTree) {
        const targetOrder = sheetTree.order;
        return targetOrder + 1;
      } else return sheetTrees.filter(sheetTree => !sheetTree.parentSheetId).length;
    } else {
      const brotherIds = sheetTrees.filter(sheetTree => sheetTree.parentSheetId === sheetId);
      return brotherIds.length;
    }
  }
  const sheetLength = getSheetLength();

  const exit = () => {
    setTimeout(() => setUnclickable(false), 300)
    onClose();
  }
  const decision = (workArea: WorkAreaStateType, kind: SheetKinds) => {
    if (unclickable) return;
    if (!bookId) {
      alert('ブックが見つかりません');
      return;
    }
    if (workArea === 'bookCover' || relation === 'noParent') {
      setUnclickable(true);
      const newSheetId = generateSheetId();
      dispatch(addSheet({name: '新しいシート', sheetKind: kind, id: newSheetId}));
      dispatch(addSheetInformation({sheetId: newSheetId, name: 'args', content: '', order: 0}));
      dispatch(addSheetTree({bookId: bookId, sheetId: newSheetId, order: sheetLength}));
      dispatch(setDefaultSheet(newSheetId));
      nodeClicked('sheet', newSheetId);
    } else if (relation === 'brother') {
      setUnclickable(true);
      const newSheetId = generateSheetId();
      const sheetTree = sheetTrees.find(sheetTree => sheetTree.sheetId === sheetId);
      if (!sheetTree) return;
      dispatch(addSheet({name: '新しいシート', sheetKind: kind, id: newSheetId}));
      dispatch(addSheetInformation({sheetId: newSheetId, name: 'args', content: '', order: 0}));
      dispatch(addSheetTree({bookId: bookId, sheetId: newSheetId, parentSheetId: sheetTree.parentSheetId, order: sheetLength}));

      sheetTreeIds.forEach((treeId, ix) => {
        const tree = sheetTrees[ix];
        if (tree && tree.parentSheetId === sheetTree.parentSheetId && tree.order >= sheetLength) {
          dispatch(updateSheetTree({ id: treeId, order: tree.order + 1 }));
        }
      })

      dispatch(setDefaultSheet(newSheetId));
    } else if (sheetId && relation === 'child') {
      setUnclickable(true);
      const newSheetId = generateSheetId();
      dispatch(addSheet({name: '新しいシート', sheetKind: kind, id: newSheetId}));
      dispatch(addSheetInformation({sheetId: newSheetId, name: 'args', content: '', order: 0}));
      dispatch(addSheetTree({bookId: bookId, sheetId: newSheetId, parentSheetId: sheetId, order: sheetLength}));
      dispatch(setDefaultSheet(newSheetId));
    } else alert('シートが見つかりません');

    exit();
  }

  return (
    <Dialog open={open} onClose={exit} maxWidth="md">
      <DialogTitle>シートを追加します</DialogTitle>
      <DialogContent style={{overflow: 'hidden'}}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <SheetTypeCard raised onClick={() => decision(workAreaState, 'story')}>
              <CardContent>
                <StoryBallotOutlinedIcon />
                <BlueTypography>ストーリーを<br />追加する</BlueTypography>
              </CardContent>
            </SheetTypeCard>
          </Grid>
          <Grid item xs={4}>
            <SheetTypeCard raised onClick={() => decision(workAreaState, 'map')}>
              <CardContent>
                <MapZoomOutMapOutlinedIcon />
                <BlueTypography>マップを<br />追加する</BlueTypography>
              </CardContent>
            </SheetTypeCard>
          </Grid>
          <Grid item xs={4}>
            <SheetTypeCard raised onClick={() => decision(workAreaState, 'grid')}>
              <CardContent>
                <GridAppsOutlinedIcon />
                <BlueTypography>グリッドを<br />追加する</BlueTypography>
              </CardContent>
            </SheetTypeCard>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={exit}>
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddSheetDialog;
